const headers = import('next/headers');

import {
  BUCKET_EXPERIMENTS_ENDPOINT,
  PREVIEW_EXPERIMENTS_ENDPOINT,
} from '@/constants/endpoints';
import {
  DEPOP_ADDITIONAL_ATTRIBUTE,
  H_BUCKET_TIME,
  H_DEPOP_DEVICE_ID,
  X_COUNTRY_CODE,
} from '@/constants/headers';
import { isServerCheck } from '@/modules/env/isServerCheck';
import {
  type PreviewExperimentsRequestBody,
  type PreviewExperimentsResponse,
} from '@/modules/experiments/aliases';
import {
  CONTROL_IDENTIFIER,
  PREVIEW_EXPERIMENTS,
} from '@/modules/experiments/config';
import { buildAdditionalAttributeHeader } from '@/modules/experiments/helpers/buildAdditionalAttributeHeader';
import { getForceVariantCookie } from '@/modules/experiments/helpers/getForceVariantCookie';
import { getForcedVariantExperiments } from '@/modules/experiments/helpers/getForcedVariantExperiments';
import { type AdditionalAttributes } from '@/modules/experiments/types';
import { typedHttp } from '@/modules/http';
import {
  type AxiosCompatibleResponse,
  type RequestConfig,
} from '@/modules/http/types';
import { isPLAReferer } from '@/modules/product/helpers/isPLAReferer';

function getOverride(experimentName: string, overrideCookie?: string) {
  try {
    if (!overrideCookie) {
      return undefined;
    }

    if (overrideCookie === '1') {
      return CONTROL_IDENTIFIER;
    }

    const experimentOverride = overrideCookie.split(',').find((experiment) => {
      const [experimentOverrideName] = experiment.split(':');
      return experimentOverrideName === experimentName;
    });

    const [, newExperimentVariant] = experimentOverride
      ? experimentOverride.split(':')
      : [];

    if (newExperimentVariant) {
      return newExperimentVariant;
    }

    return undefined;
  } catch {
    return undefined;
  }
}

async function overridePreviewExperiments(
  previewExperiments: PreviewExperimentsRequestBody
): Promise<PreviewExperimentsRequestBody> {
  const overrides = await getForcedVariantExperiments();
  const isForcedVariants =
    (await getForceVariantCookie()) === 'true' || overrides?.length;
  if (!isForcedVariants) {
    return previewExperiments;
  }

  return {
    flags: previewExperiments.flags?.map(({ name }) => {
      const override = getOverride(name, overrides);
      return {
        name,
        ...(override ? { override } : {}),
      };
    }),
  };
}

export async function getPreviewExperiments(args: {
  countryCode: string;
  persistentId: string;
  withAuth?: boolean;
}): Promise<AxiosCompatibleResponse<PreviewExperimentsResponse>> {
  const headersFunction = await headers;
  const { countryCode, persistentId, withAuth = true } = args;
  const reqHeaders: RequestConfig['headers'] = {
    [H_DEPOP_DEVICE_ID]: persistentId,
    [X_COUNTRY_CODE]: countryCode,
  };
  /**
   * This call happens server-side so need to make sure we set the device's
   * UA instead of the axios UA for this request.
   */
  if (isServerCheck()) {
    reqHeaders['user-agent'] =
      (await headersFunction.headers()).get('user-agent') || 'Unknown UA';
  } else {
    reqHeaders['user-agent'] = navigator.userAgent;
  }

  const previewExperiments =
    await overridePreviewExperiments(PREVIEW_EXPERIMENTS);

  return typedHttp.post(PREVIEW_EXPERIMENTS_ENDPOINT, previewExperiments, {
    withAuth,
    headers: reqHeaders,
    timeout: 5000,
  });
}

export type BucketUserArgs = {
  experimentNames: string[];
  countryCode: string;
  persistentId: string;
  date: number;
  referer: string | null;
  additionalAttributes?: AdditionalAttributes;
};

export function bucketUserExperiments({
  experimentNames,
  countryCode,
  persistentId,
  date,
  referer,
  additionalAttributes,
}: BucketUserArgs) {
  const isPLA = isPLAReferer({ referer });

  const body = experimentNames.map((experimentName) => ({
    name: experimentName,
  }));
  return typedHttp.post(
    BUCKET_EXPERIMENTS_ENDPOINT,
    {
      flags: body,
    },
    {
      withAuth: true,
      timeout: 5000,
      headers: {
        [H_DEPOP_DEVICE_ID]: persistentId,
        [X_COUNTRY_CODE]: countryCode,
        [H_BUCKET_TIME]: date.toString(),
        [DEPOP_ADDITIONAL_ATTRIBUTE]: buildAdditionalAttributeHeader({
          isPLA,
          additionalAttributes,
        }),
      },
    }
  );
}
